// Mappers for Shift domain: transport DTOs ↔ UI models
import { formatRut, formatToDdMmYyyy, toHHmm } from '@/utils';
import type {
  LC_GetShiftRequestsOut,
  LC_ShiftRequestOut,
  ShiftRequest,
  ShiftRequestPage,
  ShiftRequestFormData,
  LC_SubmitShiftChangeRequestIn,
  LC_CompanyShiftRequestItem,
  LC_GetCompanyShiftRequestsOut,
  LC_GetCompanyShiftRequestCountersOut,
  CompanyShiftRequest,
  CompanyShiftRequestPage,
  ShiftRequestCounters,
  ShiftRequestStatus,
  LC_CompanyShiftItem,
  LC_GetCompanyShiftsListOut,
  CompanyShiftCatalogItem,
  CompanyShiftCatalogListResult,
  LC_ScheduleItemOut,
  LC_GetSchedulesListOut,
  ScheduleOption,
  ScheduleOptionListResult,
  LC_GetCompanyShiftDetailOut,
  CompanyShiftDetail,
} from './shift.types';
import { SHIFT_REQUEST_STATUS_LABELS } from './shift.types';


/**
 * Maps backend shift request to UI model
 * Converts ISO dates to dd/MM/yyyy format and status codes to labels
 */
export const mapShiftRequestOutToModel = (dto: LC_ShiftRequestOut): ShiftRequest => ({
  id: dto.id,
  requestDate: formatToDdMmYyyy(dto.createdDate),
  comments: dto.requesterComment || '',
  dateFrom: formatToDdMmYyyy(dto.dateFrom),
  dateTo: dto.dateTo ? formatToDdMmYyyy(dto.dateTo) : '',
  status: dto.statusDescription || SHIFT_REQUEST_STATUS_LABELS[dto.status] || 'Desconocido',
  statusCode: dto.status,
  approverComment: dto.approvalComment ?? '',
  reviewDate: dto.approvalDate ? formatToDdMmYyyy(dto.approvalDate) : '',
  reviewerName: dto.reviewerName ?? '',
});

/**
 * Maps paginated backend response to UI page model
 */
export const mapShiftRequestPage = (dto: LC_GetShiftRequestsOut): ShiftRequestPage => ({
  data: dto.data?.map(mapShiftRequestOutToModel) || [],
  page: dto.pagination?.currentPage || 1,
  pageSize: dto.pagination?.pageSize || 10,
  totalRecords: dto.pagination?.totalRecords || 0,
  totalPages: dto.pagination?.totalPages || 0,
});

/**
 * Maps a company-scoped shift request item (admin listing) to its UI model.
 * Converts ISO dates to dd/MM/yyyy, status code to label, and formats the RUT.
 */
export const mapCompanyShiftRequestItem = (
  dto: LC_CompanyShiftRequestItem,
): CompanyShiftRequest => ({
  id: dto.id,
  employeeId: dto.employeeId,
  employeeRut: formatRut(dto.employeeRut),
  employeeName: dto.employeeName,
  requestDate: formatToDdMmYyyy(dto.requestDate),
  dateFrom: formatToDdMmYyyy(dto.dateFrom),
  dateTo: dto.dateTo ? formatToDdMmYyyy(dto.dateTo) : '',
  requesterComment: dto.requesterComment ?? '',
  status:
    dto.statusDescription ||
    SHIFT_REQUEST_STATUS_LABELS[dto.status as ShiftRequestStatus] ||
    String(dto.status),
  statusCode: dto.status,
  reviewDate: dto.reviewDate ? formatToDdMmYyyy(dto.reviewDate) : '',
  reviewerName: dto.reviewerName ?? '',
  approvalComment: dto.approvalComment ?? '',
});

/**
 * Maps the company-scoped paginated response to the UI page model.
 */
export const mapCompanyShiftRequestPage = (
  dto: LC_GetCompanyShiftRequestsOut,
): CompanyShiftRequestPage => ({
  data: dto.data?.map(mapCompanyShiftRequestItem) ?? [],
  page: dto.pagination?.currentPage ?? 1,
  pageSize: dto.pagination?.pageSize ?? 10,
  totalRecords: dto.pagination?.totalRecords ?? 0,
  totalPages: dto.pagination?.totalPages ?? 0,
});

/**
 * Maps the KPI status counters (ASIS-5237) from the dedicated counters endpoint
 * to the UI model. Defaults each counter to 0.
 */
export const mapShiftRequestCounters = (
  dto: LC_GetCompanyShiftRequestCountersOut,
): ShiftRequestCounters => ({
  pending: dto?.pending ?? 0,
  approved: dto?.approved ?? 0,
  rejected: dto?.rejected ?? 0,
});

const mapCompanyShiftCatalogItem = (dto: LC_CompanyShiftItem): CompanyShiftCatalogItem => ({
  id: dto.id,
  shiftCode: dto.shiftCode ?? '',
  shiftName: dto.shiftName ?? '',
  workingDayType: dto.workingDayType ?? '',
  workingDay: dto.workingDay ?? '',
  createdAt: formatToDdMmYyyy(dto.createdAt),
  status: dto.status ?? '',
  hasAssignments: dto.hasAssignments ?? false,
  isExceptionalWorkingDay: dto.isExceptionalWorkingDay ?? false,
  dtResolutionExpired: dto.dtResolutionExpired ?? false,
  dtResolutionExpiryDate: dto.dtResolutionExpiryDate ?? null,
});

/**
 * Strips the time part off a backend ISO date-time (`2026-07-01T00:00:00`) leaving
 * the `yyyy-MM-dd` that DatePicker/the form expect. Slicing the string instead of
 * building a Date keeps this timezone-safe (same pattern as
 * `employeeContractEdit.mapper.ts` / `monthlyCutsManagement.mapper.ts`).
 */
const toFormDate = (isoDate?: string): string => isoDate?.split('T')[0] ?? '';

/**
 * Maps the shift detail (ASIS-5045 — GET /attendance/shifts/{id}) to the UI model.
 */
export const mapCompanyShiftDetail = (out: LC_GetCompanyShiftDetailOut): CompanyShiftDetail => ({
  id: out.id,
  shiftCode: out.shiftCode ?? '',
  shiftName: out.shiftName ?? '',
  workingDayTypeId: out.workingDayTypeId,
  workingDayId: out.workingDayId,
  weeksCount: out.weeksCount,
  isExceptionalWorkingDay: out.isExceptionalWorkingDay ?? false,
  dtAuthorizationNumber: out.dtAuthorizationNumber ?? '',
  dtAuthorizationDate: toFormDate(out.dtAuthorizationDate),
  staticStartDate: toFormDate(out.staticStartDate),
  isEnabled: out.isEnabled,
  hasAssignments: out.hasAssignments,
  details: out.details ?? [],
});

/**
 * Maps the company shift catalog paginated response to the UI model.
 */
export const mapCompanyShiftCatalogList = (
  out: LC_GetCompanyShiftsListOut,
): CompanyShiftCatalogListResult => ({
  items: out.data?.map(mapCompanyShiftCatalogItem) ?? [],
  totalRecords: out.pagination?.totalRecords ?? 0,
});

const mapScheduleOption = (dto: LC_ScheduleItemOut): ScheduleOption => ({
  id: dto.scheduleId,
  name: dto.name,
  startTime: toHHmm(dto.startTime),
  endTime: toHHmm(dto.endTime),
  breakMinutes: dto.breakMinutes,
});

/**
 * Maps the company schedule catalog paginated response (ASIS-5052) to the UI model.
 */
export const mapScheduleOptions = (
  out: LC_GetSchedulesListOut,
): ScheduleOptionListResult => ({
  items: out.data?.map(mapScheduleOption) ?? [],
  totalRecords: out.pagination?.totalRecords ?? 0,
});

/**
 * Maps form data to backend submit request
 * Converts YYYY-MM-DD to ISO date-time format (YYYY-MM-DDTHH:mm:ss)
 */
export const mapFormDataToSubmitRequest = (
  formData: ShiftRequestFormData,
): LC_SubmitShiftChangeRequestIn => {
  // Convert YYYY-MM-DD to ISO date-time (start of day): YYYY-MM-DDTHH:mm:ss
  const toISODateTime = (dateString: string): string => 
    `${dateString}T00:00:00`
  ;

  return {
    requesterComment: formData.requesterComment || undefined,
    dateFrom: toISODateTime(formData.dateFrom),
    dateTo: toISODateTime(formData.dateTo),
  };
};
